import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, ArrowsClockwise, SealCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ClientFieldHistoryPanel
 * ------------------------
 * Unified panel showing the audit trail for one of the 5 mandatory client
 * fields (client_id / username / address / bank / upi).
 *
 * Two entry-points use this:
 *   1. "History" link above Bank / UPI section in Client Edit  → mode="picker"
 *      → Radio button on each row → activate that historical entry.
 *   2. "H" icon next to any mandatory field  → mode="audit"
 *      → Shows change count, Old vs New, changed_by. Includes a Select
 *        button per row to revert / re-activate.
 */
const FIELD_META = {
  bank: {
    label: 'Bank Account',
    render: (v = {}) => (
      <div className="text-[11px] leading-relaxed">
        <div><span className="text-slate-500">Holder:</span> <span className="font-medium">{v.account_holder_name || '-'}</span></div>
        <div><span className="text-slate-500">A/c:</span> <span className="font-mono font-medium">{v.bank_account_number || '-'}</span></div>
        <div><span className="text-slate-500">IFSC:</span> <span className="font-mono">{v.ifsc_code || '-'}</span></div>
        <div><span className="text-slate-500">Bank:</span> {v.bank_name || '-'}</div>
      </div>
    ),
    isEmpty: (v = {}) => !v.account_holder_name && !v.bank_account_number && !v.ifsc_code && !v.bank_name,
  },
  upi: {
    label: 'UPI ID',
    render: (v = {}) => <div className="text-[12px] font-mono font-medium text-emerald-800">{v.upi_id || '-'}</div>,
    isEmpty: (v = {}) => !v.upi_id,
  },
  client_id: {
    label: 'Client ID',
    render: (v = {}) => <div className="text-[12px] font-mono font-medium">{v.text || '-'}</div>,
    isEmpty: (v = {}) => !v.text,
  },
  username: {
    label: 'Username',
    render: (v = {}) => <div className="text-[12px] font-medium">{v.text || '-'}</div>,
    isEmpty: (v = {}) => !v.text,
  },
  address: {
    label: 'Address',
    render: (v = {}) => <div className="text-[11px] whitespace-pre-wrap">{v.text || '-'}</div>,
    isEmpty: (v = {}) => !v.text,
  },
  mobile_number: {
    label: 'Mobile Number',
    render: (v = {}) => <div className="text-[12px] font-mono font-medium">{v.text || '-'}</div>,
    isEmpty: (v = {}) => !v.text,
  },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const ClientFieldHistoryPanel = ({ customerId, field, mode = 'audit', actorId, actorRole, onClose, onSelected, currentValue }) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const meta = FIELD_META[field] || FIELD_META.bank;
  // Session-48g: block / delete controls only visible to super_admin.
  const isAdmin = (actorRole || '').toLowerCase() === 'super_admin' || (actorRole || '').toLowerCase() === 'admin';

  const load = useCallback(async () => {
    if (!customerId || !field) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${customerId}/field-history`, { params: { field } });
      setEntries(data?.entries || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally { setLoading(false); }
  }, [customerId, field]);

  useEffect(() => { load(); }, [load]);

  const selectEntry = async (entry) => {
    if (!entry?.id) return;
    if (entry.blocked) {
      toast.error('⚠️ Admin ne block kar rakha hai — is account me payment nahi kar sakte');
      return;
    }
    setBusyId(entry.id);
    try {
      await axios.post(`${API}/clients/${customerId}/field-history/select`, {
        field, entry_id: entry.id, actor_id: actorId,
      });
      toast.success(`${meta.label} activated`);
      if (onSelected) onSelected(entry);
      // Refresh so the radio state flips visually
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to activate');
    } finally { setBusyId(null); }
  };

  // Session-48g: Admin block / unblock / delete actions on a history entry.
  const doAdminAction = async (entry, action) => {
    if (!isAdmin) return;
    if (action === 'delete') {
      if (!window.confirm('Delete this bank account entry permanently? This cannot be undone.')) return;
    }
    setBusyId(entry.id + ':' + action);
    try {
      if (action === 'delete') {
        await axios.delete(`${API}/admin/clients/${customerId}/field-history/${entry.id}`, {
          params: { admin_id: actorId, field },
        });
        toast.success('🗑 Entry deleted');
      } else {
        await axios.post(`${API}/admin/clients/${customerId}/field-history/block`, {
          field, entry_id: entry.id, admin_id: actorId, action,
        });
        toast.success(action === 'block' ? '🚫 Account blocked' : '✅ Account unblocked');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusyId(null); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-3"
      data-testid={`field-history-panel-${field}`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-xl">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-indigo-600 font-bold">
              {mode === 'picker' ? 'Pick Active' : 'Change History'}
            </div>
            <div className="text-sm font-bold text-slate-800">{meta.label}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              className="p-1.5 rounded hover:bg-white/60"
              title="Refresh"
              data-testid={`field-history-refresh-${field}`}
            >
              <ArrowsClockwise size={16} weight="bold" className="text-slate-600" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/60"
              data-testid={`field-history-close-${field}`}
            >
              <X size={18} weight="bold" className="text-slate-700" />
            </button>
          </div>
        </div>

        <div className="px-4 py-2 bg-slate-50 text-[11px] flex items-center justify-between border-b">
          <div><span className="font-bold text-slate-600">{entries.length}</span> <span className="text-slate-500">change{entries.length === 1 ? '' : 's'} recorded</span></div>
          {mode === 'audit' && <div className="text-[10px] text-slate-400">Tap Select to revert</div>}
          {mode === 'picker' && <div className="text-[10px] text-slate-400">Tap radio to activate</div>}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400">Loading history…</div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">
              No changes recorded yet.
              {mode === 'picker' && <div className="mt-1">Save a value in the form to start building history.</div>}
            </div>
          ) : (
            entries.map((e, idx) => {
              const isActive = !!e.is_active;
              const isEmpty = meta.isEmpty(e.value || {});
              const isBlocked = !!e.blocked;
              return (
                <div
                  key={e.id}
                  className={`border-b border-slate-100 px-4 py-3 flex gap-3 items-start ${isBlocked ? 'bg-red-50/70 opacity-80' : isActive ? 'bg-emerald-50/60' : 'bg-white hover:bg-slate-50'}`}
                  data-testid={`field-history-entry-${field}-${idx}`}
                >
                  {(mode === 'picker' && (field === 'bank' || field === 'upi')) && (
                    <button
                      onClick={() => selectEntry(e)}
                      disabled={busyId === e.id || isEmpty || isBlocked}
                      className="mt-0.5 flex-shrink-0"
                      title={isBlocked ? '🚫 Admin ne block kar rakha hai' : isEmpty ? 'Empty value cannot be selected' : (isActive ? 'Currently active' : 'Make this active')}
                      data-testid={`field-history-select-radio-${field}-${idx}`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition ${isBlocked ? 'border-red-300 bg-red-100' : isActive ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white hover:border-emerald-400'}`}>
                        {isActive && !isBlocked && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        {isBlocked && <span className="text-[8px] text-red-600 font-bold">🚫</span>}
                      </div>
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {isActive && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-bold uppercase">Active</span>
                      )}
                      {isBlocked && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold uppercase"
                          title="Admin ne block kar rakha hai — koi handler is account me payment nahi kar sakta"
                          data-testid={`field-history-blocked-badge-${field}-${idx}`}
                        >🚫 Blocked by Admin</span>
                      )}
                      {idx === 0 && !isActive && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-bold uppercase">Latest</span>
                      )}
                      {e.verified && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase inline-flex items-center gap-0.5 border border-blue-300"
                          title={`Verified · used in ${e.verified_count || 1} coin cut${(e.verified_count || 1) === 1 ? '' : 's'}${e.verified_at ? ' · last on ' + fmtTime(e.verified_at) : ''}${e.verified_by_name ? ' · by ' + e.verified_by_name : ''}`}
                          data-testid={`field-history-verified-${field}-${idx}`}
                        >
                          <SealCheck size={10} weight="fill" className="text-blue-600" />
                          Verified{e.verified_count > 1 ? ` ×${e.verified_count}` : ''}
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500">{fmtTime(e.changed_at)}</span>
                    </div>
                    {meta.render(e.value || {})}
                    {(e.old_value && !meta.isEmpty(e.old_value)) && (
                      <details className="mt-1.5">
                        <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600 select-none">show previous value</summary>
                        <div className="mt-1 pl-2 border-l-2 border-slate-200 opacity-70">
                          {meta.render(e.old_value || {})}
                        </div>
                      </details>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1">
                      by <span className="font-semibold text-slate-600">{e.changed_by_name || 'System'}</span>
                      {e.changed_by_role && <span className="ml-1 text-slate-300">· {e.changed_by_role}</span>}
                    </div>
                  </div>
                  {mode === 'audit' && !isActive && !isEmpty && (
                    <button
                      onClick={() => selectEntry(e)}
                      disabled={busyId === e.id || isBlocked}
                      className="text-[10px] px-2 py-1 rounded bg-indigo-500 hover:bg-indigo-600 text-white font-bold shadow disabled:opacity-50 flex-shrink-0"
                      data-testid={`field-history-audit-select-${field}-${idx}`}
                    >
                      {busyId === e.id ? '…' : 'Select'}
                    </button>
                  )}
                  {/* Session-48g: Admin-only Block / Unblock / Delete buttons.
                      Only visible when actorRole is super_admin/admin AND the
                      field is bank/upi. Blocked accounts cannot be selected
                      as primary by any handler. */}
                  {isAdmin && (field === 'bank' || field === 'upi') && !isEmpty && (
                    <div className="flex flex-col gap-1 flex-shrink-0" data-testid={`field-history-admin-actions-${field}-${idx}`}>
                      {isBlocked ? (
                        <button
                          onClick={() => doAdminAction(e, 'unblock')}
                          disabled={busyId === e.id + ':unblock'}
                          className="text-[10px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow disabled:opacity-50"
                          data-testid={`field-history-unblock-${field}-${idx}`}
                        >✅ Unblock</button>
                      ) : (
                        <button
                          onClick={() => doAdminAction(e, 'block')}
                          disabled={busyId === e.id + ':block'}
                          className="text-[10px] px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white font-bold shadow disabled:opacity-50"
                          data-testid={`field-history-block-${field}-${idx}`}
                        >🚫 Block</button>
                      )}
                      <button
                        onClick={() => doAdminAction(e, 'delete')}
                        disabled={busyId === e.id + ':delete'}
                        className="text-[10px] px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white font-bold shadow disabled:opacity-50"
                        data-testid={`field-history-delete-${field}-${idx}`}
                      >🗑 Delete</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="px-4 py-2 border-t bg-slate-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded"
            data-testid={`field-history-done-${field}`}
          >Close</button>
        </div>
      </div>
    </div>
  );
};

export default ClientFieldHistoryPanel;
